/* SupportChatWidget — floating AI support chat for public pages.
 *
 * - Bottom-right pill button on landing / pricing / login / register pages.
 * - Click opens a card with email-capture (with consent disclaimer) on
 *   first use, then a chat interface after consent.
 * - Persists session id + email + transcript in localStorage so a refresh
 *   doesn't drop context.
 * - All admin-visible / authenticated pages skip rendering this widget
 *   (handled by mount-site in App.js, not here).
 *
 * Backend: POST /api/support/chat. Rate-limited per IP + per email.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  MessageCircle, X, Send, Loader2, Sparkles, ShieldCheck, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'pts_support_chat_v1';

const isEmailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

const persist = (state) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* quota / privacy mode — drop silently */
  }
};

const hydrate = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const SupportChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text, created_at}
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  // Hydrate from localStorage on mount (preserve email + session id + transcript).
  useEffect(() => {
    const v = hydrate();
    if (!v) return;
    setEmail(v.email || '');
    setSessionId(v.sessionId || null);
    setMessages(Array.isArray(v.messages) ? v.messages : []);
    setConsent(!!v.email && !!v.sessionId); // already consented on a previous turn
  }, []);

  // Persist whenever the parts that matter change.
  useEffect(() => {
    persist({ email, sessionId, messages });
  }, [email, sessionId, messages]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    setError(null);
    const text = draft.trim();
    if (!text) return;
    if (!isEmailValid(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!sessionId && !consent) {
      setError('Please tick the consent box to start a chat.');
      return;
    }
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: 'user', text, created_at: now }]);
    setDraft('');
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/support/chat`, {
        email: email.trim().toLowerCase(),
        message: text,
        session_id: sessionId || undefined,
        consent: !sessionId ? true : undefined,
        page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      if (r.data?.session_id && !sessionId) {
        setSessionId(r.data.session_id);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: r.data?.reply || 'No reply',
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(detail);
      // Roll back the optimistic user message so the chat doesn't show a
      // ghost user message with no AI reply.
      setMessages((prev) => prev.slice(0, -1));
      setDraft(text); // restore so user doesn't lose what they typed
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setSessionId(null);
    setMessages([]);
    setConsent(false);
    setError(null);
    persist({ email, sessionId: null, messages: [] });
  };

  // Floating launcher button
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 shadow-lg shadow-indigo-200/60 transition-all hover:scale-105"
        aria-label="Open support chat"
        data-testid="support-chat-launcher"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold hidden sm:inline">Ask AI Support</span>
      </button>
    );
  }

  const showEmailGate = !sessionId; // first run / after Reset
  const hasMessages = messages.length > 0;

  return (
    <div
      className="fixed bottom-5 right-5 z-50 w-[min(92vw,380px)] flex flex-col rounded-xl bg-white border border-zinc-200 shadow-2xl overflow-hidden"
      data-testid="support-chat-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <div>
            <div className="text-sm font-semibold leading-tight">PostToSocial Support</div>
            <div className="text-[10px] text-indigo-100">Powered by AI · typical reply in seconds</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md p-1 hover:bg-white/10"
          aria-label="Close chat"
          data-testid="support-chat-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages area */}
      <div
        ref={scrollRef}
        className="px-4 py-3 h-[380px] overflow-y-auto bg-zinc-50 text-sm space-y-3"
        data-testid="support-chat-messages"
      >
        {!hasMessages && !sending && (
          <div className="text-center text-xs text-zinc-500 mt-6">
            Hi there! 👋 Ask me anything about PostToSocial — pricing, features, setup, or any
            how-to question.
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
            data-testid={`support-chat-msg-${m.role}-${i}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border border-zinc-200 text-zinc-800'
              }`}
            >
              {m.text}
              <div
                className={`mt-1 text-[10px] ${
                  m.role === 'user' ? 'text-indigo-100/80' : 'text-zinc-400'
                }`}
              >
                {formatTime(m.created_at)}
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-zinc-200 rounded-lg px-3 py-2 text-zinc-500 text-xs flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Footer / input */}
      <div className="border-t border-zinc-200 bg-white p-3 space-y-2">
        {showEmailGate && (
          <>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              data-testid="support-chat-email-input"
              autoComplete="email"
            />
            <label className="flex items-start gap-2 text-[11px] text-zinc-600 leading-snug">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-indigo-600 flex-shrink-0"
                data-testid="support-chat-consent"
              />
              <span>
                By providing my email I give PostToSocial permission to send a response and opt in
                to future product updates. I may opt out at any time.
              </span>
            </label>
          </>
        )}
        {error && (
          <div className="flex items-start gap-1.5 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={
              showEmailGate ? 'Type your question…' : 'Reply…'
            }
            className="flex-1 px-3 py-2 text-sm rounded-md border border-zinc-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none max-h-32"
            data-testid="support-chat-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !draft.trim()}
            className="rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-300 text-white px-3 flex items-center justify-center transition-colors"
            data-testid="support-chat-send"
            aria-label="Send message"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-center justify-between text-[10px] text-zinc-400">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            Your email stays private to PostToSocial
          </span>
          {hasMessages && (
            <button
              type="button"
              onClick={reset}
              className="hover:text-zinc-600 underline-offset-2 hover:underline"
              data-testid="support-chat-reset"
            >
              Start new chat
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
